import { ReportPeriod, ReportQuery } from '../../api/reportsService';
import { getIssues, getPublications } from '../../api/magazineService';
import { getUsers } from '../../api/usersService';
import { useAuth } from '../../hooks/useAuth';
import { useEffect, useState } from 'react';

export const PERIOD_OPTIONS: { value: ReportPeriod; label: string }[] = [
  { value: '1m', label: '1 month' },
  { value: '3m', label: '3 months' },
  { value: '6m', label: '6 months' },
  { value: '1y', label: '1 year' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'all', label: 'All time' },
];

export const formatReportMoney = (amount?: number | string | null) => {
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
};

type ReportFiltersProps = {
  query: ReportQuery;
  onChange: (next: ReportQuery) => void;
  showStatus?: boolean;
  showSalesRep?: boolean;
};

const ReportFilters = ({ query, onChange, showStatus = true, showSalesRep = true }: ReportFiltersProps) => {
  const { canEditAnyDeal } = useAuth();
  const [publications, setPublications] = useState<any[]>([]);
  const [issues, setIssues] = useState<any[]>([]);
  const [salesReps, setSalesReps] = useState<any[]>([]);

  useEffect(() => {
    getPublications().then(setPublications);
  }, []);

  useEffect(() => {
    if (query.publication_id) {
      getIssues(String(query.publication_id)).then(setIssues);
    } else {
      setIssues([]);
    }
  }, [query.publication_id]);

  useEffect(() => {
    if (canEditAnyDeal && showSalesRep) {
      getUsers().then((users: any[]) => {
        const reps = (users || []).filter((u) =>
          (u.roles || []).some((r: any) => {
            const name = typeof r === 'string' ? r : r.name;
            return ['sales_manager', 'sales_rep', 'ROLE_SALES_MANAGER', 'ROLE_SALES_REP'].includes(name);
          })
        );
        setSalesReps(reps);
      });
    }
  }, [canEditAnyDeal, showSalesRep]);

  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="text-sm text-gray-600">
        Period
        <select
          className="ml-2 rounded border border-gray-300 px-2 py-1.5 text-sm"
          value={query.period || '1m'}
          onChange={(e) => onChange({ ...query, period: e.target.value as ReportPeriod })}
        >
          {PERIOD_OPTIONS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </label>

      {showStatus && (
        <label className="text-sm text-gray-600">
          Status
          <select
            className="ml-2 rounded border border-gray-300 px-2 py-1.5 text-sm"
            value={query.status || ''}
            onChange={(e) => onChange({ ...query, status: e.target.value || undefined })}
          >
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="signed">Signed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>
      )}

      <label className="text-sm text-gray-600">
        Publication
        <select
          className="ml-2 max-w-[180px] rounded border border-gray-300 px-2 py-1.5 text-sm"
          value={query.publication_id ? String(query.publication_id) : ''}
          onChange={(e) =>
            onChange({
              ...query,
              publication_id: e.target.value || undefined,
              issue_id: undefined,
            })
          }
        >
          <option value="">All</option>
          {publications.map((p) => (
            <option key={p.id} value={String(p.id)}>{p.name}</option>
          ))}
        </select>
      </label>

      <label className="text-sm text-gray-600">
        Issue
        <select
          className="ml-2 max-w-[180px] rounded border border-gray-300 px-2 py-1.5 text-sm"
          value={query.issue_id ? String(query.issue_id) : ''}
          onChange={(e) => onChange({ ...query, issue_id: e.target.value || undefined })}
          disabled={!query.publication_id}
        >
          <option value="">All</option>
          {issues.map((i) => (
            <option key={i.id} value={String(i.id)}>{i.title}</option>
          ))}
        </select>
      </label>

      {showSalesRep && canEditAnyDeal && (
        <label className="text-sm text-gray-600">
          Sales rep
          <select
            className="ml-2 max-w-[160px] rounded border border-gray-300 px-2 py-1.5 text-sm"
            value={query.sales_rep_id ? String(query.sales_rep_id) : ''}
            onChange={(e) => onChange({ ...query, sales_rep_id: e.target.value || undefined })}
          >
            <option value="">All</option>
            {salesReps.map((u) => (
              <option key={u.id} value={String(u.id)}>{u.username || u.email}</option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
};

export default ReportFilters;
